"use client";

import { useState, useEffect} from "react";
import toast from "react-hot-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Loader2 } from "lucide-react";

export const TournamentHomeAddModal = ({ isOpen, onClose, onAddTransaction, currentBalance, transactionDetails }) => {
   const [formData, setFormData] = useState({
    transactionType: "withdraw",
    amount: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [currentTransactionDetails, setCurrentTransactionDetails] = useState(transactionDetails || []);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Load the latest transaction details from localStorage when modal opens
      const savedDetails = localStorage.getItem('transactionDetailstournamenthouse');
      if (savedDetails) {
        setCurrentTransactionDetails(JSON.parse(savedDetails));
      } else {
        // Fallback to prop if no localStorage data
        setCurrentTransactionDetails(transactionDetails || []);
      }
    }
  }, [isOpen, transactionDetails]);
  

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        transactionType: "withdraw",
        amount: "",
        notes: "",
      });
      setError(null);
    }   
  }, [isOpen]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Handle transaction detail selection
  const handleTransactionDetailSelect = (selectedDetail) => {
    if (selectedDetail && selectedDetail !== "select") {
      // Replace the notes field with the selected detail (single selection)
      setFormData(prev => ({ ...prev, notes: selectedDetail }));
    }
  };


  const handleSubmit = async () => {
    // Form validation

    if (!formData.amount || isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
      setError("Please enter a valid amount");
      return;
    }

    if (!formData.notes.trim()) {
      setError("Please provide transaction details or select a transaction detail");
      return;
    }

    const amount = parseFloat(formData.amount);

    if (formData.transactionType === "withdraw" && amount > currentBalance) {
      setError("Insufficient funds for this withdrawal");
      return;
    }

    setSubmitting(true);

    try {
      const success = await onAddTransaction({
        ...formData,
        amount,
        notes: formData.notes.trim(),
      }); 

      if (success) {
        handleClose();
        toast.success("Transaction added successfully!");
      }
    } catch (error) {
      console.error("Transaction submission error:", error);
      setError("Failed to add transaction. Please try again.");
      toast.error("Failed to add transaction. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset form when modal closes
  const handleClose = () => {
    setFormData({
      transactionType: "withdraw",
      amount: "",
      notes: "",
    });
    setError(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">Add New Tournament House Fee</h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Transaction Type
            </label>
            <Select
              name="transactionType"
              value={formData.transactionType}
              onValueChange={(value) => setFormData(prev => ({ ...prev, transactionType: value }))}
            >
              <SelectTrigger className="w-full h-10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="withdraw">Withdrawal</SelectItem>
                <SelectItem value="deposit">Deposit</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Amount *
            </label>
            <Input
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              placeholder="Enter amount"
              className="w-full h-10"
              min="0.01"
              step="0.01"
              required
            />
          </div>

          {/* Transaction Details Selection Dropdown */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Select Transaction Detail
            </label>
            <Select onValueChange={handleTransactionDetailSelect}>
              <SelectTrigger className="w-full h-10">
                <SelectValue placeholder="Choose a transaction detail to add" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="select">Select a transaction detail...</SelectItem>
                {currentTransactionDetails && currentTransactionDetails.map((detail, index) => (
                  <SelectItem key={index} value={detail}>
                    {detail}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Transaction Details Input */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Transaction Details *
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder="Enter transaction details or use the dropdown above"
              className="w-full min-h-[80px] p-3 border border-gray-300 rounded-md resize-vertical focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              className="bg-yellow-500 hover:bg-yellow-600 text-black"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Adding...
                </>
              ) : (
                formData.transactionType === "deposit" ? "Add Deposit" : "Add Withdrawal"
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};